import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

/**
 * useNegotiationRealtime - Dual Realtime subscription for a single negotiation's chat view.
 *
 * Subscribes to both negotiation_messages and negotiations tables.
 * Marks unread dealer messages as read on mount.
 *
 * Ordering: the channel subscribes BEFORE the initial fetch, and the
 * reconcile-fetch runs inside the `SUBSCRIBED` callback. Marking dealer
 * messages as read is scoped to ids the fetch actually returned, never the
 * blanket `read=false` filter. Together these close the prior race where a
 * dealer reply inserted between the fetch and the channel subscription would
 * land in the DB, be cleared by `markAsRead()`, and never appear in `messages`
 * because its INSERT event was delivered before the client was listening.
 *
 * @param {string} negotiationId
 * @param {object} session - Auth session
 * @returns {{ messages: Array, negotiation: object|null, isLoading: boolean, error: Error|null }}
 */
export const useNegotiationRealtime = (negotiationId, session) => {
  const [messages, setMessages] = useState([]);
  const [negotiation, setNegotiation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!session?.user || !negotiationId) return null;

    try {
      // Fetch messages
      const { data: msgData, error: msgError } = await supabase
        .from('negotiation_messages')
        .select('*')
        .eq('negotiation_id', negotiationId)
        .order('created_at', { ascending: true });

      if (msgError) throw msgError;

      // Fetch negotiation. .maybeSingle() returns null instead of erroring
      // on 0 rows so navigation to a deleted/inaccessible negotiation surfaces
      // as a clean "not found" state rather than a thrown PostgrestError.
      const { data: negData, error: negError } = await supabase
        .from('negotiations')
        .select('*')
        .eq('id', negotiationId)
        .maybeSingle();

      if (negError) throw negError;

      if (!negData) {
        setMessages([]);
        setNegotiation(null);
        setError(new Error('negotiation_not_found'));
        return null;
      }

      // Merge the fetch result with any messages the realtime channel has
      // already pushed during/after subscribe-construction. The channel may
      // have buffered INSERTs while we were waiting on the network round-trip;
      // dedupe by id, keep chronological order. Without the merge the fetch
      // would overwrite realtime-pushed messages and we'd lose the very
      // events the subscribe-first ordering is supposed to retain.
      setMessages((prev) => {
        const byId = new Map();
        for (const m of msgData || []) byId.set(m.id, m);
        for (const m of prev || []) {
          if (!byId.has(m.id)) byId.set(m.id, m);
        }
        return Array.from(byId.values()).sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
      });
      setNegotiation(negData);
      setError(null);
      return msgData || [];
    } catch (err) {
      logger.error('[useNegotiationRealtime] Fetch error:', err);
      setError(err);

      // Skip Sentry for expected navigation events. PGRST116 = 0 rows from
      // .single()-style fetch, which we now handle via maybeSingle above but
      // a stray case (RLS race during session restore) can still surface here.
      if (err && err.code !== 'PGRST116') {
        // Wrap PostgrestError-like objects as real Error so Sentry titles are
        // readable instead of "Object captured as exception with keys: ...".
        const captured = err instanceof Error
          ? err
          : new Error(err?.message || 'useNegotiationRealtime fetch error');
        Sentry.captureException(captured, {
          tags: { hook: 'useNegotiationRealtime', supabase_code: err?.code || null },
          extra: { negotiationId, supabaseError: err },
        });
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [session, negotiationId]);

  // Mark dealer messages as read. If `dealerMessageIds` is provided, the
  // UPDATE is scoped to those ids — never the blanket unread-dealer filter.
  // Scoping prevents the prior race where a dealer reply that arrived during
  // the subscribe gap (and therefore was NOT in our `messages` state) would
  // still be flipped to read=true, hiding it forever from the client view.
  const markAsRead = useCallback(async (dealerMessageIds = null) => {
    if (!session?.user || !negotiationId) return;

    try {
      if (Array.isArray(dealerMessageIds) && dealerMessageIds.length > 0) {
        await supabase
          .from('negotiation_messages')
          .update({ read: true })
          .eq('negotiation_id', negotiationId)
          .eq('read', false)
          .eq('role', 'dealer_received')
          .in('id', dealerMessageIds);
      }
      // Always bump last_user_action_at so the cascade clock resets, even
      // when there was nothing unread to mark.
      await supabase
        .from('negotiations')
        .update({ last_user_action_at: new Date().toISOString() })
        .eq('id', negotiationId);
    } catch (err) {
      logger.error('[useNegotiationRealtime] Mark as read error:', err);
    }
  }, [session, negotiationId]);

  useEffect(() => {
    if (!session?.user || !negotiationId) {
      setMessages([]);
      setNegotiation(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Create dual subscription channel. Subscribe FIRST, then run the
    // reconcile-fetch from inside the SUBSCRIBED callback so any INSERT
    // delivered between subscribe-start and SUBSCRIBED is already buffered
    // by the channel. The fetch's `setMessages` merges with that buffer
    // (see fetchAll's dedupe-by-id), and `markAsRead` is scoped to ids the
    // fetch returned — no silent clearing of dealer replies that landed in
    // the pre-subscribe gap.
    const channelName = `negotiation-chat:${negotiationId}`;

    // Remove existing channel if any
    const existingChannels = supabase.getChannels();
    const existing = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
    if (existing) {
      supabase.removeChannel(existing);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'negotiation_messages',
          filter: `negotiation_id=eq.${negotiationId}`,
        },
        (payload) => {
          logger.log('[useNegotiationRealtime] Message INSERT:', payload.new?.role);
          setMessages((prev) => {
            // Dedup check
            if (prev.find((m) => m.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
          // Auto mark as read if it's a dealer message — scope to the single
          // delivered id so we never broaden the UPDATE filter beyond what we
          // can see in `messages`.
          if (payload.new?.role === 'dealer_received' && payload.new?.id) {
            markAsRead([payload.new.id]);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiation_messages',
          filter: `negotiation_id=eq.${negotiationId}`,
        },
        (payload) => {
          logger.log('[useNegotiationRealtime] Message UPDATE:', payload.new?.id);
          setMessages((prev) =>
            prev.map((m) => (m.id === payload.new.id ? { ...m, ...payload.new } : m))
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'negotiations',
          filter: `id=eq.${negotiationId}`,
        },
        (payload) => {
          logger.log('[useNegotiationRealtime] Negotiation UPDATE:', payload.new?.status);
          setNegotiation((prev) => (prev ? { ...prev, ...payload.new } : payload.new));
        }
      )
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('[useNegotiationRealtime] Subscribed to channel:', channelName);
          // Reconcile-fetch only after the channel is live. fetchAll merges
          // with anything realtime has already pushed; markAsRead is scoped
          // to the dealer ids we actually fetched so a reply inserted during
          // the pre-subscribe window can't be silently cleared.
          const fetched = await fetchAll();
          if (Array.isArray(fetched)) {
            const dealerIds = fetched
              .filter((m) => m.role === 'dealer_received' && m.read === false)
              .map((m) => m.id);
            markAsRead(dealerIds);
          }
        } else if (status === 'CHANNEL_ERROR') {
          logger.warn('[useNegotiationRealtime] Channel error, will re-fetch on reconnect');
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [session?.user?.id, negotiationId, fetchAll, markAsRead]);

  return { messages, negotiation, isLoading, error };
};
